<template>
  <div class="c-clientlist">
     <v-dialog
      v-model="dialog"
      max-width="600px"
      eager
    >
      <client-edit
        ref="editor"
        v-on:dismissed="dialog = false"
        v-on:saved="dialog = false"
      ></client-edit>
    </v-dialog>

    <v-row>
      <v-col>
        <div class="page-title">Liste des clients</div>
        <v-alert :value="!!error" type="error">
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>

    <v-row justify="center">
      <v-col cols="auto">
        <v-row>
          <period-selector
          ></period-selector>
        </v-row>

        <v-row>
          <v-btn
            color="primary"
            class="mb-2 mx-1"
            @click.stop="addItem"
          >
            Ajouter
          </v-btn>
          <v-btn
            color="primary"
            class="mb-2 mx-1"
            @click="exportClients"
          >
            Exporter tous
            <v-icon right>save_alt</v-icon>
          </v-btn>
        </v-row>
      </v-col>
    </v-row>

    <v-row justify="center">
      <v-col cols="auto">
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          item-key="client_id"
          single-expand
          hide-default-footer
          disable-pagination
          class="elevation-1 transparent"
        >
          <template v-slot:item="{ item, expand, isExpanded }">
            <tr class="c-clientlist__tablerow" @click.stop="expand(!isExpanded)">
              <td>{{ gid2name(item.value) }}</td>
              <td>{{ item.name }}</td>
              <td>{{ item.nb_machines }}</td>

              <td class="justify-center layout px-0">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn icon class="mx-0" v-on="on" :to="{name: 'clientMachineList', params: {clientId: item.client_id}}">
                      <v-icon color="white">video_label</v-icon>
                    </v-btn>
                  </template>
                  <span>Liste des machines</span>
                </v-tooltip>
                <v-tooltip bottom v-if="hasRole('admin')">
                  <template v-slot:activator="{ on }">
                    <v-btn icon class="mx-0" v-on="on" :to="{name: 'clientWineList', params: {clientId: item.client_id}}">
                      <v-icon color="white">description</v-icon>
                    </v-btn>
                  </template>
                  <span>Liste des vins</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn icon class="mx-0" v-on="on" @click.stop="exportOverviewWine(item)">
                      <v-icon color="white">save_alt</v-icon>
                    </v-btn>
                  </template>
                  <span>Exporter Overview Vin</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn icon class="mx-0" v-on="on" @click.stop="exportOverviewMachine(item)">
                      <v-icon color="white">save_alt</v-icon>
                    </v-btn>
                  </template>
                  <span>Exporter Overview Machine</span>
                </v-tooltip>
                <v-tooltip bottom v-if="hasRole('admin')">
                  <template v-slot:activator="{ on }">
                    <v-btn icon class="mx-0" v-on="on" @click.stop="editItem(item)">
                      <v-icon color="teal">edit</v-icon>
                    </v-btn>
                  </template>
                  <span>Editer</span>
                </v-tooltip>
                <v-tooltip bottom v-if="hasRole('admin')">
                  <template v-slot:activator="{ on }">
                    <v-btn icon class="mx-0" v-on="on" @click.stop="deleteItem(item)">
                      <v-icon color="pink">delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Supprimer</span>
                </v-tooltip>
              </td>
            </tr>
          </template>

          <template v-slot:expanded-item="{ item }">
            <td :colspan="headers.length">
              <v-card flat class="grey darken-2">
                <v-card-title primary-title>
                  <h4 class="headline mb-0">{{ item.name }}</h4>
                </v-card-title>
                <v-card-text>
                  <div>
                    <span>{{ `${item.address.address}, ${item.address.zipCode} ${item.address.city} -- ${item.address.country}` }}</span><br/>
                    <span>N° tél : {{ item.phone_number }}</span><br/>
                    <span>Mail : {{ item.email }}</span><br/>
                  </div>
                </v-card-text>
              </v-card>
            </td>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import ExportAPI from '@/api/export';

import ClientEdit from '@/components/ClientEdit';
import PeriodSelector from '@/components/PeriodSelector';

import CSVExport from '@/mixins/CSVExport';
import ErrorManagement from '@/mixins/ErrorManagement';
import PeriodListener from '@/mixins/PeriodListener';
import RoleFiltering from '@/mixins/RoleFiltering';

import { CLIENT_DELETE, CLIENT_LIST } from '@/store/actions/client';


export default {
  name: 'ClientList',

  mixins: [
    CSVExport,
    ErrorManagement,
    PeriodListener,
    RoleFiltering
  ],

  components: {
    ClientEdit,
    PeriodSelector
  },

  data: () => ({
    loading: false,
    headers: [
      { text: 'Groupe', value: 'group_id' },
      { text: 'Nom', value: 'name' },
      { text: 'Machines installées', value: 'nb_machines' },
      { text: 'Actions', value: 'action', sortable: false }
    ],

    dialog: false
  }),

  computed: {
    ...mapState({
      items: state => state.client.clients,
      defaultItem: state => state.client.defaultItem
    })
  },

  mounted () {
    this.$store.dispatch(CLIENT_LIST)
      .catch((err) => {
        this.setError(err);
      });
  },

  methods: {
    gid2name (val) {
      if (!val) {
        return 'Indépendant';
      }

      const grp = this.$store.getters.groupById(val);
      return grp ? grp.name : '';
    },

    addItem () {
      this.$refs.editor.edit(null);
      this.dialog = true;
    },
    editItem (item) {
      this.$refs.editor.edit(item);
      this.dialog = true;
    },
    deleteItem (item) {
      if (!confirm('Etes-vous sûr de vouloir supprimer ce client ?')) {
        return;
      }

      this.$store.dispatch(CLIENT_DELETE, item.client_id)
        .catch((err) => {
          this.setError(err);
        });
    },

    exportClients () {
      this.saveCSV(ExportAPI.getClients(), 'export_clients');
    },
    exportOverviewWine (item) {
      this.saveCSV(ExportAPI.getOverviewWine(
        this.period,
        this.period_from,
        this.period_to,
        item.client_id,
        null
      ), 'export_overview_wine');
    },
    exportOverviewMachine (item) {
      this.saveCSV(ExportAPI.getOverviewMachine(
        this.period,
        this.period_from,
        this.period_to,
        item.client_id
      ), 'export_overview_machine');
    }
  }
};
</script>

<style lang="scss" scoped>
.c-clientlist {
  &__tablerow {
    cursor: pointer;
  }
}
</style>
